 <template>
  <v-container grid-list-md fluid>
    <progress-bar></progress-bar>
    <v-toolbar
      dark
      height="66"
      color="secondary"
      class="mb-4 no-right-padding"
    >
      <v-toolbar-title>
        <v-btn small icon :to="{name: 'projects-list' }">
          <v-icon>arrow_back</v-icon>
        </v-btn>
        {{ project.name }}
      </v-toolbar-title>
      <v-toolbar-items v-if="canEdit(project) && isDesktop">
        <v-btn icon small color="info" :to="{name: 'projects-edit', params: {project_id: project.id} }">
          <v-icon size="18">edit</v-icon>
        </v-btn>
      </v-toolbar-items>
      <v-divider v-if="isDesktop" class="mx-2" inset vertical></v-divider>
      <v-spacer></v-spacer>
      <v-layout v-if="isDesktop">
        <v-spacer></v-spacer>
        <v-flex sm3 xs12>
          <v-text-field
            v-model="filter.search"
            clearable
            hide-details
            label="Search by title"
            outline
          />
        </v-flex>
        <v-flex sm2 xs12>
          <v-select
            v-model="filter.users"
            :items="project.users"
            clearable
            hide-details
            item-text="name"
            item-value="id"
            label="Users"
            multiple
            outline
          />
        </v-flex>
        <v-flex sm2 xs12>
          <v-select
            v-model="filter.labels"
            :items="project.labels"
            clearable
            hide-details
            item-text="name"
            item-value="id"
            label="Labels"
            multiple
            outline
          />
        </v-flex>
        <v-flex sm2 xs12>
          <v-select
            v-model="filter.columns"
            :items="project.columns"
            clearable
            hide-details
            item-text="name"
            item-value="id"
            label="Columns"
            multiple
            outline
          />
        </v-flex>
      </v-layout>
      <v-menu :close-on-content-click="false" v-else bottom offset-y right>
        <v-btn slot="activator" small icon class='ma-0'>
          <v-icon>more_vert</v-icon>
        </v-btn>
        <v-card>
          <v-card-text>
            <v-list>
              <v-list-tile
                v-if="hasFeature(project, $features.PROJECT_REPORTS)"
                :to="{name: 'projects-report', params: {project_id: project.id} }"
              >
                <v-list-tile-title>Reports</v-list-tile-title>
              </v-list-tile>
              <v-list-tile
                v-if="canEdit(project)"
                :to="{name: 'projects-edit', params: {project_id: project.id} }"
              >
                <v-list-tile-title>Edit</v-list-tile-title>
              </v-list-tile>
              <remove-dialog v-if="canDelete(project)" :yes="() => remove(project)">
                <v-list-tile>
                  <v-list-tile-title>
                    Delete
                  </v-list-tile-title>
                </v-list-tile>
              </remove-dialog>
            </v-list>
            <v-text-field
              v-model="filter.search"
              clearable
              outline
              label="Search by title"
            />
            <v-select
              v-model="filter.columns"
              outline
              label="Columns"
              multiple
              :items="project.columns"
              clearable
              item-text="name"
              item-value="id"
            />
            <v-select
              v-model="filter.labels"
              outline
              label="Labels"
              multiple
              :items="project.labels"
              clearable
              item-text="name"
              item-value="id"
            />
            <v-select
              v-model="filter.users"
              outline
              label="Users"
              multiple
              :items="project.users"
              clearable
              item-text="name"
              item-value="id"
              hide-details
            />
          </v-card-text>
        </v-card>
      </v-menu>
      <v-toolbar-items v-if="isDesktop">
        <v-btn small icon @click="view = 'list'" :color="view == 'list' ? 'info' : 'secondary'">
          <v-icon size="18">list</v-icon>
        </v-btn>
        <v-btn small icon @click="view = 'columns'" :color="view == 'columns' ? 'info' : 'secondary'">
          <v-icon size="18">view_column</v-icon>
        </v-btn>
      </v-toolbar-items>
    </v-toolbar>

    <list-view
      v-show="view === 'list'"
      @refresh="refresh"
      :data="data"
    />

    <column-view
      v-show="view === 'columns'"
      @refresh="refresh"
      :data="data"
    />

    <v-speed-dial v-model="actionButton" direction="top" bottom right fixed>
      <template slot="activator">
        <v-btn v-model="actionButton" fab amsll dark>
          <v-icon>unfold_more_double</v-icon>
          <v-icon>unfold_less_double</v-icon>
        </v-btn>
      </template>
      <v-btn amsll icon title="New task" :to="{name: 'tasks-form', params: {project_id: project.id, task_id: 0} }" color="success">
        <v-icon>add</v-icon>
      </v-btn>
      <v-btn amsll icon @click="showTrashedTasks" :title="filter.onlyTrashed ? 'Don’t show archived tasks' : 'Show archive tasks'" :color="filter.onlyTrashed ? 'accent' : 'info'">
        <v-icon v-if="filter.onlyTrashed">visibility_off</v-icon>
        <v-icon v-else>visibility</v-icon>
      </v-btn>
      <v-btn amsll icon :to="{name: 'projects-report', params: {project_id: project.id} }" v-if="featureAllowed($features.PROJECT_REPORTS)" title="Project reports" color="info">
        <v-icon>timeline</v-icon>
      </v-btn>
    </v-speed-dial>
  </v-container>
</template>

<script>
  import * as mixins from '@/mixins/'
  import list from '@/components/tasks/list'
  import column from '@/components/tasks/column'

  export default {
    name: 'ProjectsViewPage',
    mixins: [mixins.removable, mixins.filter, mixins.preloader, mixins.user, mixins.history],
    data() {
      return {
        api: this.$api.tasks,
        view: 'list',
        project: {users: [], user: {}},
        data: [],
        filter: {
          search: "",
          users: [],
          columns: [],
          labels: [],
          onlyTrashed: ''
        },
        actionButton: false,
        settings: [],
        currentProjectSettings: {}
      }
    },
    methods: {
      loadProject() {
        return this.$api.projects.get(this.$route.params.project_id).then(({data}) => {
          document.title = data.name
          this.project = {
            ...data,
            users: data.instance_users.concat(data.instance_user)
          }
        })
      },
      load() {
        let params = {
          unlimit: 1,
          projects: [this.$route.params.project_id],
          order: 'sort_id',
          ...this.collectFilter()
        }

        return this.api.list(params).then(({data}) => {
          this.data = this.project.columns.map(column => {
            return {
              ...column,
              is_opened: this.isCollapsePanelOpened(column.id),
              tasks: data.filter(task => task.column_id == column.id)
            }
          })

          this.loadSettings()
          this.saveSettings(this.data)
        })
      },
      isCollapsePanelOpened(columnId) {
        const closedByDefault = [1, 4, 5]
        return closedByDefault.some(item => item === columnId) ? [false] : [true]
      },
      changeFilter(id) {
        if (this.filter.users.indexOf(id) >= 0) {
          this.filter.users = this.filter.users.filter(item => item != id)
        } else {
          this.filter.users.push(id)
        }
      },
      showTrashedTasks() {
        this.filter.onlyTrashed = this.filter.onlyTrashed ? '' : true
      },
      changeQuery() {
        this.$router.push({path: this.$route.path, query: this.collectFilter()}, () => {
          this.updateTitle("[SL] " + this.project.name)
        })
      },
      isUserFilterActive(id) {
        return this.filter.users.indexOf(id) >= 0
      },
      refresh() {
        this.background(this.load())
      },
      onMounted() {
        this.background(this.loadProject().then(() => {
          this.filter = {
            ...this.filter,
            ...this.$route.query,
            columns: [this.$route.query.columns].flat().filter(item => item).map(item => item && parseInt(item)),
            users: [this.$route.query.users].flat().filter(item => item).map(item => item && parseInt(item)),
            tags: [this.$route.query.labels].flat().filter(item => item).map(item => item && parseInt(item)),
          }
          this.load()
        }))
      },
      saveSettings(newData) {
        const result = []
        const data = [
          {
            project_id: this.project.id,
            view: this.view,
            collapsedPanels: newData.map(item => {
              return {
                is_opened: item.is_opened,
                columnId: item.id
              }
            })
          },
          ...this.settings,
        ].filter(element => {
          const isDuplicate = result.includes(element.project_id);
          if (!isDuplicate) {
            result.push(element.project_id);
            return true;
          }

          return false;
        });

        localStorage.setItem('settings', JSON.stringify(data))
      },
      loadSettings() {
        const localSettings = localStorage.getItem('settings')
        if (localSettings) {
          this.settings = JSON.parse(localSettings)
          this.currentProjectSettings = this.settings.find(item => item.project_id === this.project.id)

          if (this.currentProjectSettings) {
            this.view = this.currentProjectSettings.view
            this.data.forEach((column) => {
              column.is_opened = this.currentProjectSettings.collapsedPanels.find(item => item.columnId === column.id).is_opened || [false]
            })
          }
        }
      }
    },
    watch: {
      filter: {
        handler() {
          this.changeQuery()
          this.background(this.load())
        },
        deep: true
      },
      data: {
        handler(newVal) {
          this.saveSettings(newVal)
        },
        deep: true
      },
      '$route.params.project_id'() {
        this.onMounted()
      },
      view() {
        this.saveSettings(this.data)
      }
    },
    mounted() {
      this.onMounted()
    },
    components: {
      'list-view': list,
      'column-view': column,
    },
  }
</script>
